import { useContext, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { db } from '../../firebase/config'
import { client } from '../../hooks/Client'
import { useFirestoreGeneral } from '../../firebase/useFirestore'
import { callFetchHierarchyData } from '../../hooks/useHierarchyData'
import useInView from '../../hooks/useInView'
import buildCorrelationMatrix from '../../helpers/analysisHelpers/buildCorrelationMatrix'
import { DEFAULT_MIN_PAIRS } from '../../helpers/analysisHelpers/correlation'
import summarizeAnchorCorrelations from '../../helpers/analysisHelpers/summarizeAnchorCorrelations'
import notDeleted from '../../helpers/notDeleted'
import DeepAnalysisAIContext from '../../StateManagment/DeepAnalysisAIContext'
import DeepAnalysisPeriodContext from '../../StateManagment/DeepAnalysisPeriodContext'
import ComparisonFrame from '../dataVisualizations/ComparisonFrame'
import periodRangeToMs from '../../helpers/compareHelpers/periodRangeToMs'
import sideSummariesForAI from '../../helpers/compareHelpers/sideSummariesForAI'
import DeepAnalysisSectionHeader from './DeepAnalysisSectionHeader'

// Bucket r into a CSS class for the cell background (no inline styles).
const cellClass = (cell) => {
    if (!cell || cell.insufficient || cell.r === null) return 'deep-analysis-matrix-cell--insufficient'
    const strength = Math.abs(cell.r) >= 0.7 ? 3 : Math.abs(cell.r) >= 0.4 ? 2 : 1
    return cell.r >= 0
        ? `deep-analysis-matrix-cell--pos-${strength}`
        : `deep-analysis-matrix-cell--neg-${strength}`
}

/** Every pair below the minimum → the matrix is all grey and needs a reason. */
const isAllInsufficient = (matrix) => matrix.effects.length > 1 && matrix.effects.every((row) =>
    matrix.effects.every((col) =>
        row.id === col.id || matrix.cells[row.id]?.[col.id]?.insufficient
    )
)

/**
 * One period's matrix table plus its all-grey explanation.
 *
 * @param {object} props
 * @param {{effects: Array<{id: string, name: string}>, cells: Object<string, Object<string, {r: number|null, n: number, insufficient: boolean}>>}} props.matrix - Built matrix for this period
 * @param {string} props.anchorId - Anchor effect ID ('' on a standalone matrix)
 * @param {boolean} props.hasPeriod - The matrix covers a chosen period (changes the all-grey wording)
 * @param {(idA: string, idB: string) => void} props.onOpenPair - Opens the clicked pair
 */
const MatrixPanel = ({ matrix, anchorId, hasPeriod, onOpenPair }) => (
    <>
        <div className='deep-analysis-matrix-wrapper'>
            <table className='deep-analysis-matrix'>
                <thead>
                    <tr>
                        <th></th>
                        {matrix.effects.map((e) => (
                            <th
                                key={e.id}
                                title={e.name}
                                className={e.id === anchorId ? 'deep-analysis-matrix-head--anchor' : ''}
                            >
                                {e.name}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {matrix.effects.map((rowEffect) => (
                        <tr key={rowEffect.id}>
                            <th
                                title={rowEffect.name}
                                className={rowEffect.id === anchorId ? 'deep-analysis-matrix-head--anchor' : ''}
                            >
                                {rowEffect.name}
                            </th>
                            {matrix.effects.map((colEffect) => {
                                const cell = matrix.cells[rowEffect.id]?.[colEffect.id]
                                const isDiagonal = rowEffect.id === colEffect.id
                                return (
                                    <td
                                        key={colEffect.id}
                                        className={`deep-analysis-matrix-cell ${isDiagonal ? 'deep-analysis-matrix-cell--diagonal' : cellClass(cell)}`}
                                        title={cell ? `n = ${cell.n}` : ''}
                                        onClick={() => onOpenPair(rowEffect.id, colEffect.id)}
                                    >
                                        {isDiagonal
                                            ? '—'
                                            : cell && cell.r !== null
                                                ? cell.r.toFixed(2)
                                                : '·'}
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        {isAllInsufficient(matrix) && (
            <p className='deep-analysis-empty'>
                {hasPeriod ? 'Te weinig gedeelde deelnemers in deze periode om correlaties te berekenen' : 'Nog te weinig gedeelde deelnemers om correlaties te berekenen'}: per paar
                effecten zijn minimaal {DEFAULT_MIN_PAIRS} deelnemers nodig die bij beide
                effecten aan hun antwoorden gekoppeld zijn (via de deelnemer-volgmodule).
            </p>
        )}
    </>
)

/**
 * Effect × effect correlation matrix over all MSI effects of the tenant.
 * One fetchHierarchyData call per effect (results are cached in the hook's
 * module cache), then a pure matrix build. Cell click opens the pair as a
 * scatterplot on the effect deep-analysis page.
 *
 * With an anchor effect (effect deep-analysis page) the anchor's row comes
 * first and is highlighted; a cell click on any pair that includes the anchor
 * stays on the page and opens the pair scatter via `?compare=`.
 *
 * Follows the page period (DeepAnalysisPeriodContext): one fetch per effect
 * per shown period, and one matrix per period side by side when comparing.
 * A cell click keeps the period in the url.
 * AI-CONTEXT: See docs/features/deep-analysis-period.md
 *
 * @param {object} props
 * @param {object} [props.effect] - Anchor OutputEffects doc (effect page); omit for a standalone matrix
 */
const CorrelationMatrixSection = ({ effect }) => {
    const history = useHistory()
    const location = useLocation()
    const anchorId = effect?.ID || ''
    const msiEffects = useFirestoreGeneral('OutputEffects', 'Standard', 'true')
    const period = useContext(DeepAnalysisPeriodContext)
    const compare = Boolean(period?.compareActive)

    const [data, setData] = useState(
        /** @type {{effects: object[], recordsBySide: {A: Object<string, object[]>, B?: Object<string, object[]>}} | null} */ (null)
    )
    const [error, setError] = useState('')

    // Load-on-demand: the matrix fires one hierarchy fetch per MSI effect,
    // so wait until the section scrolls into view (same pattern as the
    // data-heavy dashboard cards).
    const [sectionRef, inView] = useInView({ rootMargin: '200px', triggerOnce: true })

    // Stable key — the snapshot array is a fresh reference on every change.
    // AI-CONTEXT: See docs/pitfalls.md#state-useeffect-inline-filter
    const effectsKey = Array.isArray(msiEffects)
        ? JSON.stringify(msiEffects.map((e) => e.ID).sort())
        : ''
    // The periods to fetch, as a stable key (same reason).
    const sidesKey = JSON.stringify(compare
        ? { A: period.rangeA, B: period.rangeB }
        : { A: period?.rangeA || null })

    // The first indicator per effect doesn't depend on the period, so a
    // period change only refetches the records below.
    const [indicators, setIndicators] = useState(
        /** @type {{effects: object[], list: Array<[string, object|undefined]>} | null} */ (null)
    )
    useEffect(() => {
        if (!inView) return
        if (!Array.isArray(msiEffects) || msiEffects.length < 2) return

        let cancelled = false
        const effects = msiEffects
        setIndicators(null)
        setError('')

        Promise.all(effects.map(async (effect) => {
            const fieldsSnapshot = await db.collection('QuestionnaireFields')
                .where('CompagnyID', '==', client)
                .where('EffectId', 'array-contains', effect.ID)
                .get()
            const fields = []
            fieldsSnapshot.docs.filter((doc) => notDeleted(doc.data())).forEach((doc) => fields.push(doc.data()))
            // Same "first indicator" as the scatter/pair sections, which
            // order by IndicatorPosition — client-side sort so docs
            // missing the field aren't excluded by a Firestore orderBy.
            fields.sort((a, b) => (Number(a.IndicatorPosition) || 0) - (Number(b.IndicatorPosition) || 0))
            return [effect.ID, fields[0]]
        }))
            .then((list) => { if (!cancelled) setIndicators({ effects, list }) })
            .catch((err) => {
                console.error('CorrelationMatrixSection indicators failed:', err)
                if (!cancelled) setError('De correlatiematrix kon niet worden geladen.')
            })

        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectsKey, inView])

    useEffect(() => {
        if (!indicators) return

        let cancelled = false
        const sides = JSON.parse(sidesKey)
        // A new period: the old numbers belong to the old period.
        setData(null)
        setError('')

        const recordsFor = async (range) => {
            const { startMs, endMs } = periodRangeToMs(range)
            const entries = await Promise.all(indicators.list.map(async ([effectId, indicator]) => {
                if (!indicator) return [effectId, []]
                const isMsiMatrix = (indicator.Type === 'matrix-one' || indicator.Type === 'matrix-multiple')
                    && indicator.Standard === 'true'
                const { records } = await callFetchHierarchyData({
                    syncType: isMsiMatrix ? 'MSIEffect' : 'Question',
                    id: indicator.ID,
                    startDate: startMs,
                    endDate: endMs,
                })
                return [effectId, records]
            }))
            return Object.fromEntries(entries)
        }

        Promise.all([recordsFor(sides.A), 'B' in sides ? recordsFor(sides.B) : null])
            .then(([A, B]) => {
                if (cancelled) return
                setData({ effects: indicators.effects, recordsBySide: B ? { A, B } : { A } })
            })
            .catch((err) => {
                console.error('CorrelationMatrixSection load failed:', err)
                if (!cancelled) setError('De correlatiematrix kon niet worden geladen.')
            })

        return () => { cancelled = true }
    }, [indicators, sidesKey])

    const matrices = useMemo(() => {
        if (!data) return null
        const build = (records) => buildCorrelationMatrix(data.effects, records, anchorId ? { anchorId } : {})
        return {
            A: build(data.recordsBySide.A),
            B: data.recordsBySide.B ? build(data.recordsBySide.B) : null,
        }
    }, [data, anchorId])

    // Publish the anchor's correlations for Decca (deep-analysis AI section),
    // per period when comparing (sideSummariesForAI).
    const aiContext = useContext(DeepAnalysisAIContext)
    const publish = aiContext?.publish
    const forAI = useMemo(() => {
        if (!anchorId || !matrices) return null
        return sideSummariesForAI({
            A: summarizeAnchorCorrelations(matrices.A, anchorId),
            B: matrices.B ? summarizeAnchorCorrelations(matrices.B, anchorId) : null,
        }, period)
    }, [anchorId, matrices, period])
    useEffect(() => {
        if (!publish || !forAI) return
        publish('correlatiesMetAndereEffecten', forAI)
    }, [publish, forAI])

    // Keeps the page's other query params — the period above all.
    // AI-CONTEXT: See docs/pitfalls.md#routing-url-built-here-read-by-position-there
    const openPair = (idA, idB) => {
        if (idA === idB) return
        const params = new URLSearchParams(location.search)
        // Pairs that include the anchor open below on the same page.
        const anchorPair = anchorId && (idA === anchorId || idB === anchorId)
        const pageId = anchorPair ? anchorId : idA
        params.set('compare', anchorPair ? (idA === anchorId ? idB : idA) : idB)
        history.push(`/${client}/deepanalysis/effect/${pageId}?${params.toString()}`)
    }

    if (Array.isArray(msiEffects) && msiEffects.length < 2) {
        // Same root element as the main branch so React reuses the DOM node
        // and the in-view observer (attached on mount) stays valid.
        return (
            <div className='deep-analysis-section' ref={sectionRef}>
                <p className='deep-analysis-empty'>
                    Voor een correlatiematrix zijn minimaal twee MSI-effecten nodig.
                </p>
            </div>
        )
    }

    const panel = (range, side) => (
        matrices?.[side]
            ? <MatrixPanel matrix={matrices[side]} anchorId={anchorId} hasPeriod={Boolean(range)} onOpenPair={openPair} />
            : null
    )

    return (
        <div className='deep-analysis-section' ref={sectionRef}>
            <DeepAnalysisSectionHeader
                kicker='Correlatie'
                title='Correlatiematrix'
                description='Per paar effecten: de samenhang (r, van -1 tot 1) tussen de scores van dezelfde deelnemers. Klik op een cel om het paar als puntenwolk te openen. Grijze cellen hebben te weinig gedeelde deelnemers.'
            />
            {error && <p className='deep-analysis-error'>{error}</p>}
            {!error && !matrices && <p>De matrix wordt berekend…</p>}
            {!error && matrices && (compare
                ? <ComparisonFrame rangeA={period.rangeA} rangeB={period.rangeB} renderViz={panel} />
                : panel(period?.rangeA || null, 'A'))}
        </div>
    )
}

export default CorrelationMatrixSection
